import React, { useEffect, useMemo, useState } from 'react';
import { ProductTextureEditingModel } from '../../../entities/product-texture-editing-model';
import { BwjSidePanel } from '../../../../framework/components/bwj-side-panel';
import { BwjButtonType } from '../../../../framework/components/bwj-button';
import * as formService from '../../form-services/variant-editor';
import { IProductTexture } from '../../../../entities-generated/common/product-texture';
import { BwjImageBrowser } from '../../../../framework/components/bwj-image-browser';
import { BwjGrid, Row, Col } from '../../../../framework/components/bwj-layout';
import { Spacing } from '../../../../framework/components/bwj-layout/styles';
import { BwjSingleSelectField } from '../../../../framework/components/bwj-select-field/single';
import { BwjTextField } from '../../../../framework/components/bwj-text-field';
import { IStormDoorColorOption } from '../../../../entities-generated/common/storm-door-color-option';
import { BwjLazyImage } from '../../../../framework/components/bwj-lazy-image';

interface IProductVariantEditorPanelProps {
  invokePanelDisplay: ProductTextureEditingModel | undefined;
  onUpdateVariant: (variant: IProductTexture) => void;
}

const subscriberId = 'Component.ProductVariantEditorPanel';

export const ProductVariantEditorPanel: React.FunctionComponent<IProductVariantEditorPanelProps> = (props) => {
  const { invokePanelDisplay: editModel } = props;
  const [showPanel, setShowPanel] = useState(false);
  const [imageDisplayIndex, setImageDisplayIndex] = useState(0);
  const formModel = formService.useFormModel(subscriberId);
  const hasUnsavedChanges = formService.useHasUnsavedChanges(formModel, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const images = useMemo(() => formService.productImageUrls.getOutputValue(), [formModel]);

  useEffect(() => {
    if(!!editModel) {
      formService.initializeService(editModel);
      setShowPanel(true);
    } else {
      setShowPanel(false);
    }
  }, [editModel]);

  // adjust image index when the number of images are less than the current index
  useEffect(() => {
    if(imageDisplayIndex >= images.length) {
      setImageDisplayIndex(images.length > 0 ? images.length - 1 : 0);
    }
  }, [images, imageDisplayIndex]);

  const onSaveChanges = () => {
    formService.validateAndUpdateModel()
    .then(result => {
      const variant = formService.toProductTexture();
      if(result.isValid) {
        props.onUpdateVariant(variant);
        setShowPanel(false);
      } else {
        const errs = formService.getFormErrors();
        console.log(errs);
      }
    });
  }

  return (
    <BwjSidePanel
      showPanel={showPanel}
      footerButtons={[{
        buttonStyle: BwjButtonType.PrimaryStandard,
        text: 'Save changes',
        disabled: hasUnsavedChanges === false,
        onClick: onSaveChanges,
      }, {
        buttonStyle: BwjButtonType.DefaultStandard,
        text: 'Cancel',
        onClick: () => setShowPanel(false),
      }]}
      onDismiss={() => setShowPanel(false)}
    >
      <BwjGrid>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.name.id}
              required={true}
              label={formService.name.description}
              onUpdate={formService.name.onUpdate}
              input={formModel.name}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjSingleSelectField<IStormDoorColorOption>
              id={formService.colorFamily.id}
              required={true}
              label={formService.colorFamily.description}
              onUpdate={formService.colorFamily.onUpdate}
              input={formModel.colorFamily}
              options={formService.colorFamily.options}
              optionDisplayKey={'description'}
              optionValueKey={'id'}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.swatchUrl.id}
              required={false}
              label={formService.swatchUrl.description}
              onUpdate={formService.swatchUrl.onUpdate}
              input={formModel.swatchUrl}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjLazyImage
              src={formModel.swatchUrl.value}
              loadImage={true}
              styles={{root: {width: 400, height: 200}}}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12}>
            <BwjTextField
              id={formService.productImageUrls.id}
              required={false}
              label={formService.productImageUrls.description}
              onUpdate={formService.productImageUrls.onUpdate}
              input={formModel.productImageUrls}
              multiline={true}
            />
          </Col>
        </Row>
        <Row bottomSpacing={Spacing.Large}>
          <Col small={12} large={6}>
            <BwjImageBrowser
              id={`${formService.productImageUrls.id}-image-viewer`}
              images={images.map(x => { return { src: x }; })}
              displayIndex={imageDisplayIndex}
              loadImages={true}
              imageWidth={400}
              onDisplayIndexChanged={index => setImageDisplayIndex(index)}
            />
          </Col>
        </Row>
      </BwjGrid>
    </BwjSidePanel>
  );
};
